import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  currentMonthSP,
  formatBRL,
  formatMonthLabel,
  todaySP,
  type AccountsResponse,
  type InvoiceDetail,
  type PayInvoiceBody,
  type Ym,
  type Ymd,
} from '@lira/shared';
import { apiGet, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { toast } from '@/stores/toast';
import { MoneyInput, Select, Sheet, resolveIcon, type SelectOption } from '@/components/ui';
import { DateField } from '@/features/transactions/DateField';

interface PayInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  cardId: string;
  cardName: string;
  invoiceMonth: Ym;
  /** Valor em aberto da fatura (default do campo). */
  remainingCents: number;
  /** Conta vinculada = default da origem do pagamento. */
  linkedAccountId: string | null;
}

/** Paga (total ou parcial) a fatura de um mês. POST /cards/:id/invoices/:month/pay. */
export function PayInvoiceModal({
  open,
  onClose,
  cardId,
  cardName,
  invoiceMonth,
  remainingCents,
  linkedAccountId,
}: PayInvoiceModalProps) {
  const queryClient = useQueryClient();
  const [amountCents, setAmountCents] = useState(remainingCents);
  const [accountId, setAccountId] = useState(linkedAccountId ?? '');
  const [date, setDate] = useState<Ymd>(todaySP());

  useEffect(() => {
    if (!open) return;
    setAmountCents(remainingCents);
    setAccountId(linkedAccountId ?? '');
    setDate(todaySP());
  }, [open, remainingCents, linkedAccountId]);

  const accountsQuery = useQuery({
    queryKey: ['accounts', 'compose'],
    queryFn: () => apiGet<AccountsResponse>('/accounts'),
    enabled: open,
  });
  const accountOptions = useMemo<SelectOption<string>[]>(
    () =>
      (accountsQuery.data?.accounts ?? [])
        .filter((a) => !a.archived)
        .map((a) => ({ value: a.id, label: a.name, icon: resolveIcon(a.icon), color: a.color })),
    [accountsQuery.data],
  );

  const mutation = useMutation({
    mutationFn: () => {
      const body: PayInvoiceBody = { amountCents, date };
      if (accountId) body.accountId = accountId;
      return apiPost<{ invoice: InvoiceDetail }>(`/cards/${cardId}/invoices/${invoiceMonth}/pay`, body);
    },
    onSuccess: ({ invoice }) => {
      queryClient.setQueryData(['card-invoice', cardId, invoiceMonth], invoice);
      void queryClient.invalidateQueries({ queryKey: ['cards'] });
      void queryClient.invalidateQueries({ queryKey: ['card-invoice', cardId, invoiceMonth] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast('Pagamento registrado.', 'success');
      onClose();
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const canSave = amountCents > 0 && amountCents <= remainingCents && !!accountId && !mutation.isPending;

  return (
    <Sheet open={open} onClose={onClose} title="Pagar fatura" maxWidth={480}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) mutation.mutate();
        }}
        className="space-y-5"
      >
        <p className="text-[13px] text-muted">
          {cardName} · fatura de{' '}
          <span className="font-semibold capitalize text-ink">
            {formatMonthLabel(invoiceMonth)}
          </span>
          <br />
          Em aberto: <span className="font-semibold text-ink">{formatBRL(remainingCents)}</span>
        </p>

        <div className="rounded-lg bg-cards/10 px-4 py-4">
          <label htmlFor="pay-amount" className="mb-0.5 block text-[13px] text-muted">
            Valor do pagamento
          </label>
          <MoneyInput
            id="pay-amount"
            valueCents={amountCents}
            onChange={setAmountCents}
            accent="cards"
            size="lg"
            aria-label="Valor do pagamento"
          />
          {amountCents > remainingCents && (
            <p className="mt-1 text-[13px] text-alert">
              Valor acima do que resta na fatura ({formatBRL(remainingCents)}).
            </p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-[13px] text-muted">Pagar com a conta</label>
          <Select<string>
            value={accountId}
            options={accountOptions}
            onChange={setAccountId}
            placeholder="Selecione a conta"
            aria-label="Conta de pagamento"
          />
        </div>

        <DateField value={date} onChange={setDate} viewedMonth={currentMonthSP()} accent="cards" />

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-full px-5 text-sm font-semibold uppercase tracking-wide text-muted transition-colors hover:bg-canvas hover:text-ink"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="flex h-11 items-center gap-2 rounded-full bg-cards px-6 text-sm font-semibold uppercase tracking-wide text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Pagar
          </button>
        </div>
      </form>
    </Sheet>
  );
}
